import React, { useState, useEffect, useCallback } from 'react';
import { 
  History, Clock, User, Shield, FileText, MessageSquare, 
  Upload, Trash2, Sparkles, GitBranch, Download, AlertTriangle,
  ChevronDown, ChevronUp, Loader2, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';

const ACTION_ICONS = {
  REPORT_CREATED: FileText,
  REPORT_VIEWED: FileText,
  STATUS_CHANGED: Shield,
  SEVERITY_CHANGED: AlertTriangle,
  CATEGORY_CHANGED: FileText,
  MESSAGE_SENT: MessageSquare,
  ACKNOWLEDGMENT_SENT: FileText,
  ATTACHMENT_UPLOADED: Upload,
  ATTACHMENT_DELETED: Trash2,
  AI_ANALYSIS: Sparkles,
  AI_SUGGESTIONS_APPROVED: Sparkles,
  AI_SUGGESTIONS_REJECTED: Sparkles,
  WORKFLOW_STARTED: GitBranch,
  WORKFLOW_APPROVED: GitBranch,
  WORKFLOW_REJECTED: GitBranch,
  REPORT_EXPORTED: Download,
  REPORT_PDF_EXPORTED: Download,
  CONFLICT_DETECTED: AlertTriangle,
  ASSIGNMENT_CHANGED: User
};

const ACTION_COLORS = {
  REPORT_CREATED: 'bg-blue-100 text-blue-700',
  STATUS_CHANGED: 'bg-purple-100 text-purple-700',
  SEVERITY_CHANGED: 'bg-orange-100 text-orange-700',
  MESSAGE_SENT: 'bg-green-100 text-green-700',
  ACKNOWLEDGMENT_SENT: 'bg-emerald-100 text-emerald-700',
  ATTACHMENT_UPLOADED: 'bg-cyan-100 text-cyan-700',
  ATTACHMENT_DELETED: 'bg-red-100 text-red-700',
  AI_ANALYSIS: 'bg-violet-100 text-violet-700',
  AI_SUGGESTIONS_APPROVED: 'bg-emerald-100 text-emerald-700',
  AI_SUGGESTIONS_REJECTED: 'bg-amber-100 text-amber-700',
  WORKFLOW_STARTED: 'bg-indigo-100 text-indigo-700',
  WORKFLOW_APPROVED: 'bg-green-100 text-green-700',
  WORKFLOW_REJECTED: 'bg-red-100 text-red-700',
  REPORT_EXPORTED: 'bg-slate-100 text-slate-700',
  REPORT_PDF_EXPORTED: 'bg-slate-100 text-slate-700',
  CONFLICT_DETECTED: 'bg-red-100 text-red-700',
  ASSIGNMENT_CHANGED: 'bg-blue-100 text-blue-700'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const AuditLogEntry = ({ log, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = ACTION_ICONS[log.action] || History;
  const colorClass = ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700';
  
  const hasDetails = log.details && Object.keys(log.details).length > 0;
  
  return (
    <div className={`relative pl-8 pb-4 ${!isLast ? 'border-l-2 border-slate-200 ml-3' : 'ml-3'}`}>
      {/* Timeline dot */}
      <div className={`absolute left-0 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
        <Icon className="w-3 h-3" />
      </div>
      
      {/* Content */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 ml-4 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900 text-sm">
                {log.action_label || log.action}
              </span>
              <Badge variant="outline" className="text-xs">
                {log.role_label || log.user_role || 'Sistema'}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <User className="w-3 h-3" />
              <span>{log.user_name || 'Sistema'}</span>
              <span>•</span>
              <Clock className="w-3 h-3" />
              <span>{formatDate(log.created_at)}</span>
            </div>
          </div>
          
          {hasDetails && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>
        
        {/* Expanded details */}
        {expanded && hasDetails && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-600 space-y-1">
              {Object.entries(log.details).map(([key, value]) => (
                <div key={key} className="flex">
                  <span className="font-medium w-24">{key}:</span>
                  <span className="text-slate-800">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AuditLogPanel = ({ reportId, isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.getReportAuditLogs(reportId);
      setLogs(response.data.logs || []);
    } catch (err) {
      setError('Erro ao carregar logs de auditoria');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    if (isOpen && reportId) {
      fetchLogs();
    }
  }, [isOpen, reportId, fetchLogs]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Registo de Auditoria
          </DialogTitle>
          <DialogDescription>
            Histórico completo de ações nesta denúncia
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">{error}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Sem registos de auditoria</p>
            </div>
          ) : (
            <div className="py-4">
              {logs.map((log, index) => (
                <AuditLogEntry 
                  key={log.id} 
                  log={log} 
                  isLast={index === logs.length - 1}
                />
              ))}
            </div>
          )}
        </div>
        
        <div className="border-t pt-3 mt-2 flex items-center justify-between text-xs text-slate-500">
          <span>{logs.length} registo(s) encontrado(s)</span>
          <span>Os logs são incluídos no PDF exportado</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Export both the panel and a simple button trigger
export const AuditLogButton = ({ reportId, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setIsOpen(true)}
        className={className}
        data-testid="audit-log-btn"
      >
        <History className="w-4 h-4 mr-2" />
        Auditoria
      </Button>
      <AuditLogPanel 
        reportId={reportId} 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
      />
    </>
  );
};

export default AuditLogPanel;
